import * as THREE from 'three';
import type { AABB } from './collisions/AABBCollider';
import type { Checkpoint } from '../race/RaceTypes';
import { loadCarRaceAssetManifest, normalizeMapAssetUrls } from '../../assets/AssetConfig';

export class SimpleMap {
  readonly group = new THREE.Group();
  readonly colliders: AABB[] = [];
  readonly bounds = { width: 220, height: 220 };
  readonly startPoint = new THREE.Vector3(-10, 0.05, 40);
  readonly spawnPoint = new THREE.Vector3(-10, 0.6, 40);
  readonly finishPoint = new THREE.Vector3(10, 0.05, 40);
  readonly checkpoints: Checkpoint[] = [];
  private readonly roadMaterials: THREE.MeshStandardMaterial[] = [];
  private readonly textureLoader = new THREE.TextureLoader();

  constructor() {
    const groundGeo = new THREE.PlaneGeometry(this.bounds.width, this.bounds.height);
    const groundMat = new THREE.MeshStandardMaterial({ roughness: 0.95, metalness: 0, color: 0x10151f });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    this.group.add(ground);

    this.addRoadStrip(0, 0, 180, 10);
    this.addRoadStrip(0, -40, 120, 10);
    this.addRoadStrip(40, 0, 10, 120);

    this.addBarrier(-30, 0.5, 20, 4, 1, 20);
    this.addBarrier(30, 0.5, 20, 4, 1, 20);
    this.addBarrier(0, 0.5, -70, 60, 1, 4);

    this.addBlock(-60, 2, -20, 14, 4, 14);
    this.addBlock(-50, 2, 25, 18, 4, 10);
    this.addBlock(55, 2, -30, 16, 4, 16);

    this.addStartFinish();
    this.addCheckpoint('cp-1', new THREE.Vector3(-20, 0.05, 10));
    this.addCheckpoint('cp-2', new THREE.Vector3(40, 0.05, 20));
    this.addCheckpoint('cp-3', new THREE.Vector3(10, 0.05, -60));
    this.addCheckpoint('finish', this.finishPoint, 4.2, 0x60ff88);

    this.applyExternalAssets(groundMat);
  }

  private applyExternalAssets(groundMat: THREE.MeshStandardMaterial) {
    loadCarRaceAssetManifest()
      .then((manifest) => {
        const mapAssets = normalizeMapAssetUrls(manifest.map);
        this.applyGroundTexture(groundMat, mapAssets.terrainTexture);
        this.applyRoadTexture(mapAssets.roadTexture);
        this.addOsmOverlay(mapAssets.overlay);
        this.loadExternalColliders(mapAssets.colliders);
      })
      .catch(() => {
        this.applyGroundTexture(groundMat);
        this.applyRoadTexture();
        this.addOsmOverlay();
        this.loadExternalColliders();
      });
  }

  private loadExternalColliders(collidersUrl = `${process.env.PUBLIC_URL || ''}/assets/car-race/osm/colliders.json`) {
    fetch(collidersUrl)
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (!payload || !Array.isArray(payload.colliders)) return;

        payload.colliders.forEach((entry: any) => {
          if (!entry || !entry.min || !entry.max) return;
          const min = new THREE.Vector3(Number(entry.min.x) || 0, Number(entry.min.y) || 0, Number(entry.min.z) || 0);
          const max = new THREE.Vector3(Number(entry.max.x) || 0, Number(entry.max.y) || 0, Number(entry.max.z) || 0);
          this.colliders.push({ min, max });
        });
      })
      .catch(() => {
        // Kollisionsdatei ist optional
      });
  }

  private applyGroundTexture(
    groundMat: THREE.MeshStandardMaterial,
    grassUrl = `${process.env.PUBLIC_URL || ''}/assets/car-race/textures/terrain/grass_diffuse.jpg`
  ) {
    this.textureLoader.load(
      grassUrl,
      (texture) => {
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.repeat.set(12, 12);
        groundMat.map = texture;
        groundMat.color.setHex(0xffffff);
        groundMat.needsUpdate = true;
      },
      undefined,
      () => {
        // Fallback auf bestehende Materialfarbe
      }
    );
  }

  private applyRoadTexture(roadUrl = `${process.env.PUBLIC_URL || ''}/assets/car-race/textures/road/asphalt_diffuse.jpg`) {
    if (!this.roadMaterials.length) return;

    this.textureLoader.load(
      roadUrl,
      (texture) => {
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.repeat.set(8, 2);
        this.roadMaterials.forEach((material) => {
          material.map = texture;
          material.color.setHex(0xffffff);
          material.needsUpdate = true;
        });
      },
      undefined,
      () => {
        // Fallback auf bestehende Materialfarbe
      }
    );
  }

  private addOsmOverlay(osmUrl = `${process.env.PUBLIC_URL || ''}/assets/car-race/osm/track_overlay.png`) {
    this.textureLoader.load(
      osmUrl,
      (texture) => {
        const overlayMat = new THREE.MeshBasicMaterial({
          map: texture,
          transparent: true,
          opacity: 0.65,
          depthWrite: false
        });
        const overlay = new THREE.Mesh(
          new THREE.PlaneGeometry(this.bounds.width, this.bounds.height),
          overlayMat
        );
        overlay.rotation.x = -Math.PI / 2;
        overlay.position.y = 0.015;
        this.group.add(overlay);
      },
      undefined,
      () => {
        // OSM-Overlay ist optional
      }
    );
  }

  private addStartFinish() {
    const startGeo = new THREE.RingGeometry(2.8, 3.3, 32);
    const startMat = new THREE.MeshBasicMaterial({ color: 0xffc65c, transparent: true, opacity: 0.7, side: THREE.DoubleSide });
    const startRing = new THREE.Mesh(startGeo, startMat);
    startRing.rotation.x = -Math.PI / 2;
    startRing.position.copy(this.startPoint);
    this.group.add(startRing);

    const finishGeo = new THREE.RingGeometry(3.4, 4.0, 32);
    const finishMat = new THREE.MeshBasicMaterial({ color: 0x60ff88, transparent: true, opacity: 0.6, side: THREE.DoubleSide });
    const finishRing = new THREE.Mesh(finishGeo, finishMat);
    finishRing.rotation.x = -Math.PI / 2;
    finishRing.position.copy(this.finishPoint);
    this.group.add(finishRing);
  }

  private addCheckpoint(id: string, position: THREE.Vector3, radius = 3, color = 0x2ee6ff) {
    const ringGeo = new THREE.RingGeometry(radius - 0.4, radius, 32);
    const ringMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.6, side: THREE.DoubleSide });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.copy(position);
    this.group.add(ring);

    this.checkpoints.push({ id, position, radius });
  }

  private addRoadStrip(x: number, z: number, w: number, h: number) {
    const geo = new THREE.PlaneGeometry(w, h);
    const mat = new THREE.MeshStandardMaterial({ roughness: 0.85, metalness: 0, color: 0x2b2f38 });
    this.roadMaterials.push(mat);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(x, 0.01, z);
    mesh.receiveShadow = true;
    this.group.add(mesh);
  }

  private addBarrier(x: number, y: number, z: number, w: number, h: number, d: number) {
    const geo = new THREE.BoxGeometry(w, h, d);
    const mat = new THREE.MeshStandardMaterial({ roughness: 0.5, metalness: 0.1, color: 0xa02a2a });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(x, y, z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.group.add(mesh);

    const min = new THREE.Vector3(x - w / 2, y - h / 2, z - d / 2);
    const max = new THREE.Vector3(x + w / 2, y + h / 2, z + d / 2);
    this.colliders.push({ min, max });
  }

  private addBlock(x: number, y: number, z: number, w: number, h: number, d: number) {
    const geo = new THREE.BoxGeometry(w, h, d);
    const mat = new THREE.MeshStandardMaterial({ roughness: 0.7, metalness: 0, color: 0x485062 });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(x, y, z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.group.add(mesh);

    const min = new THREE.Vector3(x - w / 2, y - h / 2, z - d / 2);
    const max = new THREE.Vector3(x + w / 2, y + h / 2, z + d / 2);
    this.colliders.push({ min, max });
  }
}
